import type {
  EditableComponent,
  EditableComponentsMap,
  OneOfPromptOption,
  PromptOption,
  PromptOptionValue,
  SelectedPromptOptionValues,
} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {PromptOptionSelectorLayoutType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {
  getEditableComponentCheckboxContent,
  getPromptOptionEnum,
  getPromptOptionSelectorLayoutType,
  getPromptOptionTitle,
  getPromptOptionValueContent,
  getPromptOptionValueDescription,
} from '@Libraries/ai-redesign-prompt-box-library';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {CheckboxFilters} from '@Components/checkbox-filters';
import type {Filter, FiltersHashmap} from '@Components/checkbox-filters/checkbox-filters.types';
import styles from './ai-redesign-contextual-menu.module.scss';

interface AiRedesignContextualMenuProps {
  promptOptionsToUse: PromptOption[];
  selectedPromptOptionValues: SelectedPromptOptionValues;
  onSelectedPromptOptionValuesChange: (promptOptionValues: SelectedPromptOptionValues) => void;
  contextualMenuWidth: number;
  editableComponentsMap: EditableComponentsMap;
  onEditableComponentsMapChange: (editableComponentsMap: EditableComponentsMap) => void;
}

export function AIRedesignContextualMenu({selectedPromptOptionValues, editableComponentsMap, ...props}: AiRedesignContextualMenuProps): ReactElement {
  const generatePromptOptionValueKey = (option: PromptOption, optionValue: PromptOptionValue): string => {
    return `${optionValue}-${option}-list-item`;
  };

  const handleSelectedPromptOptionValueChange = (changedProperty: OneOfPromptOption): void => {
    const {option, value} = changedProperty;

    const updatedSelectedPromptOptionValues = {
      ...selectedPromptOptionValues,
      [option]: value,
    };

    props.onSelectedPromptOptionValuesChange(updatedSelectedPromptOptionValues);
  };

  const getSelectorHeading = (promptOption: PromptOption): ReactElement => {
    const title = getPromptOptionTitle(promptOption);
    return <Text bold val={title} size={TextSize.XXSMALL} className={styles.contextualMenuHeading} />;
  };

  const getSelectorContainerClasses = (promptOption: PromptOption): string => {
    const selectorLayoutType = getPromptOptionSelectorLayoutType(promptOption);

    switch (selectorLayoutType) {
      case PromptOptionSelectorLayoutType.PILLS:
        return styles.contextualMenuPillContent;
      case PromptOptionSelectorLayoutType.CARDS:
        return 'flexbox flex-column gap-2';
    }
  };

  const getPillButton = (optionValue: PromptOptionValue, promptOption: PromptOption, selectedValueKey: string): ReactElement => {
    const key = generatePromptOptionValueKey(promptOption, optionValue);
    const text = getPromptOptionValueContent(optionValue);

    return (
      <PillButton
        key={key}
        text={text}
        isSelected={key === selectedValueKey}
        size={PillButtonSize.SMALL}
        onClick={() => {
          const changedProperty = {
            option: promptOption,
            value: optionValue,
          };

          handleSelectedPromptOptionValueChange(changedProperty);
        }}
        textSize={TextSize.XXSMALL}
      />
    );
  };

  const getCard = (optionValue: PromptOptionValue, promptOption: PromptOption, selectedValueKey: string): ReactElement => {
    const key = generatePromptOptionValueKey(promptOption, optionValue);
    const text = getPromptOptionValueContent(optionValue);
    const description = getPromptOptionValueDescription(optionValue);
    const isSelected = key === selectedValueKey;
    return (
      <div
        key={key}
        className={`${styles.card} ${isSelected ? styles.selected : ''}`}
        onClick={() => {
          const changedProperty = {
            option: promptOption,
            value: optionValue,
          };

          handleSelectedPromptOptionValueChange(changedProperty);
        }}
      >
        <Text bold val={text} size={TextSize.XXSMALL} className={isSelected ? 'content-primary' : ''} />
        <Text val={description} size={TextSize.XXSMALL} className={isSelected ? 'content-primary' : ''} />
      </div>
    );
  };

  const getPromptOptionValues = (option: PromptOption): PromptOptionValue[] => {
    return Object.values(getPromptOptionEnum(option)).map((enumMember: PromptOptionValue) => {
      return enumMember;
    });
  };

  const getOptionValues = (promptOption: PromptOption): ReactElement => {
    const selectorLayoutType = getPromptOptionSelectorLayoutType(promptOption);
    const optionValues = getPromptOptionValues(promptOption);
    const selectedValue = selectedPromptOptionValues[promptOption];
    const selectedValueKey = generatePromptOptionValueKey(promptOption, selectedValue);

    switch (selectorLayoutType) {
      case PromptOptionSelectorLayoutType.PILLS:
        return <>{optionValues.map((optionValue) => getPillButton(optionValue, promptOption, selectedValueKey))}</>;
      case PromptOptionSelectorLayoutType.CARDS:
        return <>{optionValues.map((optionValue) => getCard(optionValue, promptOption, selectedValueKey))}</>;
    }
  };

  const getPromptOptionValueSelector = (promptOption: PromptOption): ReactElement => {
    return (
      <div className={'flexbox flex-column gap-2'}>
        {getSelectorHeading(promptOption)}
        <div className={getSelectorContainerClasses(promptOption)}>{getOptionValues(promptOption)}</div>
      </div>
    );
  };

  const getPromptOptionValueSelectors = (): ReactElement => {
    return (
      <>
        {props.promptOptionsToUse.map((option) => {
          return <div key={option}>{getPromptOptionValueSelector(option)}</div>;
        })}
      </>
    );
  };

  const getEditableComponentCheckboxFilters = (editableComponentsMap: EditableComponentsMap): Filter[] => {
    const checkboxes: Filter[] = [];
    const editableComponents = Object.keys(editableComponentsMap);

    editableComponents.map((editableComponent) => {
      checkboxes.push(getEditableComponentCheckboxContent(editableComponent as EditableComponent, editableComponentsMap, styles.checkboxIcon, styles.checkboxLabel));
    });

    return checkboxes;
  };

  const getEditableComponentCheckboxes = (editableComponentsMap: EditableComponentsMap): ReactElement => {
    return (
      <div className="flexbox gap-7">
        <CheckboxFilters
          filters={getEditableComponentCheckboxFilters(editableComponentsMap)}
          onChange={(newState: FiltersHashmap) => {
            props.onEditableComponentsMapChange(newState as EditableComponentsMap);
          }}
          minSelectionNumber={1}
        />
      </div>
    );
  };

  const getEditableComponentSelector = (): ReactElement | null => {
    if (Object.keys(editableComponentsMap).length === 1) {
      return null;
    }

    return (
      <div>
        <Text bold val={window.i18next.t('pmwjs_update_the_following')} size={TextSize.XSMALL} className={styles.contextualMenuHeading} />
        {getEditableComponentCheckboxes(editableComponentsMap)}
      </div>
    );
  };

  const getContextualMenu = (): ReactElement => {
    return (
      <div style={{minWidth: props.contextualMenuWidth}} className={'flexbox flex-column gap-4 spacing-p-t-3 spacing-p-r-4 spacing-p-b-5 spacing-p-l-4'}>
        {getEditableComponentSelector()}
        {getPromptOptionValueSelectors()}
      </div>
    );
  };

  return getContextualMenu();
}
